import { useEffect } from 'react';

interface PageMeta {
  title: string;
  description: string;
  /** Absolute canonical URL for this route. */
  canonical: string;
}

/**
 * Lightweight per-route head management without adding a dependency.
 * Sets <title>, <meta name="description">, <link rel="canonical">,
 * og:title, og:description, og:url, twitter:title, twitter:description.
 */
export function usePageMeta({ title, description, canonical }: PageMeta) {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const previousTitle = document.title;
    document.title = title;

    const setMeta = (selector: string, attr: 'name' | 'property', key: string, value: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', value);
      return { el, prev };
    };

    const restores: Array<() => void> = [];
    const track = (r: { el: HTMLMetaElement; prev: string | null }) => {
      restores.push(() => {
        if (r.prev === null) r.el.remove();
        else r.el.setAttribute('content', r.prev);
      });
    };

    track(setMeta('meta[name="description"]', 'name', 'description', description));
    track(setMeta('meta[property="og:title"]', 'property', 'og:title', title));
    track(setMeta('meta[property="og:description"]', 'property', 'og:description', description));
    track(setMeta('meta[property="og:url"]', 'property', 'og:url', canonical));
    track(setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title));
    track(setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description));

    let canonicalEl = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const canonicalExisted = !!canonicalEl;
    const prevHref = canonicalEl?.getAttribute('href') ?? null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonical);

    return () => {
      document.title = previousTitle;
      restores.forEach((fn) => fn());
      if (canonicalEl) {
        if (!canonicalExisted) canonicalEl.remove();
        else if (prevHref !== null) canonicalEl.setAttribute('href', prevHref);
      }
    };
  }, [title, description, canonical]);
}
